import React, { FunctionComponent, useState, useEffect, useCallback, useMemo } from 'react';

import classnames from 'classnames';
import { Form } from 'react-bootstrap';
import { trackPromise } from 'react-promise-tracker';

import { Overlay } from 'core/overlay/Overlay';
import { TargetInterest } from 'social/publisher/Publisher.types';
import { fetchFacebookTargetInterests } from 'social/publisher/PublisherFetchers';

interface Props {
    interests: TargetInterest[] | undefined;
    tenantId: number | undefined;
    save: any;
}

interface ShowNestedOptions {
    [index: string]: boolean;
}

interface DisabledOptions {
    [index: string]: boolean;
}

interface SelectedOptions {
    [index: string]: boolean;
}

interface InterestMap {
    [index: string]: TargetInterest;
}

const buildInterestMap = (interests: TargetInterest[]): InterestMap => {
    const result: InterestMap = {};
    const build = (vals: TargetInterest[]): void => {
        for (let i = 0; i < vals.length; i++) {
            const curr = vals[i];

            result[`${curr.key}`] = curr;

            if (curr.values && curr.values.length > 0) {
                build(curr.values);
            }
        }
    };

    build(interests);
    return result;
};
const getNestedInterestKeys = (interest: TargetInterest): string[] => {
    const result: string[] = [];
    const getKeys = (vals: TargetInterest[]): void => {
        for (let i = 0; i < vals.length; i++) {
            const curr = vals[i];

            result.push(`${curr.key}`);

            if (curr.values && curr.values.length > 0) {
                getKeys(curr.values);
            }
        }
    };

    if (interest.values && interest.values.length > 0) {
        getKeys(interest.values);
    }
    return result;
};
const OVERLAY_AREA = 'social-publisher-post-target-interests';

export const InterestField: FunctionComponent<Props> = ({ interests, tenantId, save }) => {
    const [facebookTargetInterests, setFacebookTargetInterests] = useState<TargetInterest[]>([]);
    // control if an interest with nested options is expanded or collpased
    const [showNestedOptions, setShowNestedOptions] = useState<ShowNestedOptions>({});
    const interestMap = useMemo(() => {
        let map: InterestMap = {};

        if (facebookTargetInterests && facebookTargetInterests.length > 0) {
            map = buildInterestMap(facebookTargetInterests);
        }
        return map;
    }, [facebookTargetInterests]);
    const selectedOptions: SelectedOptions = {};
    const initialDisabledOptions: DisabledOptions = {};

    if (interests && interests.length > 0) {
        interests.forEach((el) => {
            const interest = interestMap[`${el.key}`];

            if (interest) {
                const nestedKeys = getNestedInterestKeys(interest);

                nestedKeys.forEach((key) => {
                    initialDisabledOptions[key] = true;
                });

                selectedOptions[`${el.key}`] = true;
            }
        });
    }

    const [disabledOptions, setDisabledOptions] = useState<DisabledOptions>(initialDisabledOptions);
    const loadTargetInterests = useCallback(async () => {
        const data = await trackPromise(fetchFacebookTargetInterests(tenantId), OVERLAY_AREA);

        if (data) {
            setFacebookTargetInterests(data);
        }
    }, [tenantId]);

    const handleToggleOption = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const isOptionSelected = event.target.checked;
        const interestKey = event.target.value;
        const interest = interestMap[interestKey];
        const nestedKeys = getNestedInterestKeys(interest);
        const newSelectedOptions = { ...selectedOptions };
        const newDisabledOptions = { ...disabledOptions };

        if (isOptionSelected) {
            newSelectedOptions[interestKey] = true;
            nestedKeys.forEach((el) => {
                newDisabledOptions[el] = true;
                newSelectedOptions[el] = false;
            });
        } else {
            newSelectedOptions[interestKey] = false;
            nestedKeys.forEach((el) => {
                newDisabledOptions[el] = false;
            });
        }

        setDisabledOptions(newDisabledOptions);

        const newInterests: TargetInterest[] = [];

        Object.keys(newSelectedOptions).forEach((el) => {
            if (interestMap[el] && newSelectedOptions[el]) {
                const newInterest = interestMap[el];

                // set nested values as [] when saving
                newInterests.push({
                    name: newInterest.name,
                    key: newInterest.key,
                    audienceSize: newInterest.audienceSize,
                    values: [],
                });
            }
        });

        save(newInterests);
    };

    const getTargetInterestOptions = (interestsData: TargetInterest[]): JSX.Element[] => {
        const options: JSX.Element[] = [];
        let nestedLevel = 0;

        const buildInterestOptions = (
            parentLevelOptions: JSX.Element[],
            targetInterests: TargetInterest[],
            parentKey?: string,
        ): void => {
            const currentLevelOptions: JSX.Element[] = [];
            const currentLevel = nestedLevel;

            targetInterests.forEach((el): void => {
                const hasNestedValues = el.values && el.values.length > 0;
                // convert interest key to string;
                const interestKey = `${el.key}`;
                const newOption = (
                    <div
                        key={interestKey}
                        className={`interest-${interestKey} post-target-field-option`}
                        role="button"
                        aria-label="Expand Option"
                        tabIndex={0}
                        onKeyPress={(): void => {
                            // Do Nothing
                        }}
                        onClick={(): void => {
                            if (hasNestedValues) {
                                const newShowNestedOptions = { ...showNestedOptions };
                                const isExpanded = !!showNestedOptions[interestKey];

                                newShowNestedOptions[interestKey] = !isExpanded;
                                setShowNestedOptions(newShowNestedOptions);
                            }
                        }}
                    >
                        <Form.Group
                            controlId={interestKey}
                            onClick={(e: React.SyntheticEvent): void => {
                                e.stopPropagation();
                            }}
                        >
                            <Form.Check
                                type="checkbox"
                                label={el.name}
                                checked={!!selectedOptions[interestKey]}
                                disabled={!!disabledOptions[interestKey]}
                                onChange={handleToggleOption}
                                value={interestKey}
                            />
                        </Form.Group>
                        {hasNestedValues && (
                            <div className="post-target-field-option-icon">
                                <i
                                    className={showNestedOptions[interestKey] ? 'icon-angle-down' : 'icon-angle-right'}
                                />
                            </div>
                        )}
                    </div>
                );

                currentLevelOptions.push(newOption);

                if (hasNestedValues) {
                    nestedLevel += 1;
                    buildInterestOptions(currentLevelOptions, el.values as TargetInterest[], interestKey);
                }
            });

            if (parentKey) {
                parentLevelOptions.push(
                    <div
                        key={`${parentKey}-${currentLevel}`}
                        className={classnames(`interest-${parentKey} level-${currentLevel}`, {
                            show: showNestedOptions[parentKey],
                        })}
                    >
                        {currentLevelOptions}
                    </div>,
                );
            } else {
                parentLevelOptions.push(...currentLevelOptions);
            }

            nestedLevel -= 1;
        };

        buildInterestOptions(options, interestsData);

        return options;
    };

    useEffect(() => {
        loadTargetInterests();
    }, [loadTargetInterests]);

    return (
        <>
            <Overlay area={OVERLAY_AREA} />
            {getTargetInterestOptions(facebookTargetInterests)}
        </>
    );
};
